// Pure helpers for the 3-tier reports data flow (minimal | preview | full).
// Ported from the cleancy mobile app's DataContext so the web honors the same
// tier-aware merge invariant: a status-only payload is a pin-status OVERLAY and
// must never downgrade a report already loaded as preview/full.

import { ReportData, ReportDataLevel } from "../types/global.d";
import { API_CONFIG } from "../constants";

const LEVEL_RANK: Record<ReportDataLevel, number> = {
  minimal: 0,
  preview: 1,
  full: 2,
};

/**
 * Infer a report's data tier from the fields it carries. Keeps tiering correct
 * even if the server returns a richer payload than expected (pre-v3 fallback).
 */
export const inferReportDataLevel = (report: any): ReportDataLevel => {
  // Minimal: map pin only (no user, no images, no preview thumbnail).
  if (!report.imagePreview && !report.images?.length && !report.user) {
    return "minimal";
  }
  // Full: anything carrying detail fields.
  if (
    report.comment !== undefined ||
    report.cleaned ||
    (report.images?.length && report.images.length > 1) ||
    report.area !== undefined
  ) {
    return "full";
  }
  // Otherwise a single-image preview row.
  return "preview";
};

/** Stamp dataLevel (+ legacy isMinimal flag) onto a freshly fetched report. */
export const stampDataLevel = (report: any): ReportData => {
  const dataLevel = inferReportDataLevel(report);
  return { ...report, dataLevel, isMinimal: dataLevel !== "full" };
};

const higherLevel = (
  a: ReportDataLevel | undefined,
  b: ReportDataLevel | undefined
): ReportDataLevel => {
  const rankA = LEVEL_RANK[a ?? "minimal"];
  const rankB = LEVEL_RANK[b ?? "minimal"];
  return rankA >= rankB ? a ?? "minimal" : b ?? "minimal";
};

export interface StatusOnlyMergeOptions {
  isIncremental?: boolean;
  removedReports?: string[];
}

/**
 * Merge a status-only (minimal-tier) sync payload into the current reports as a
 * pin-status overlay:
 *  - existing reports: update only status/sortTimestamp, keep their tier + data
 *  - brand-new ids: add as minimal pins
 *  - non-incremental snapshot: prune ids absent from the payload (authoritative)
 *  - honor explicit removedReports
 * Never downgrades a report already at preview/full. Returns the same array
 * reference when nothing changed so React can skip re-renders.
 */
export const mergeStatusOnly = (
  prevReports: ReportData[],
  incoming: ReportData[],
  { isIncremental = false, removedReports = [] }: StatusOnlyMergeOptions = {}
): ReportData[] => {
  const incomingById = new Map<string, ReportData>();
  incoming.forEach((report) => {
    if (report && report._id) {
      incomingById.set(report._id, report);
    }
  });

  let changed = false;

  let next = prevReports.map((existing) => {
    const update = incomingById.get(existing._id);
    if (!update) {
      return existing; // keep — never downgrade or drop here
    }
    const nextSortTimestamp = update.sortTimestamp ?? existing.sortTimestamp;
    if (
      existing.status !== update.status ||
      existing.sortTimestamp !== nextSortTimestamp
    ) {
      changed = true;
      return {
        ...existing,
        status: update.status,
        sortTimestamp: nextSortTimestamp,
      };
    }
    return existing;
  });

  // Add brand-new pins as minimal tier.
  const existingIds = new Set(prevReports.map((r) => r._id));
  incoming.forEach((report) => {
    if (report && report._id && !existingIds.has(report._id)) {
      next.push({ ...report, dataLevel: "minimal", isMinimal: true });
      changed = true;
    }
  });

  // A non-incremental snapshot is authoritative about which reports exist.
  if (!isIncremental) {
    const beforeLength = next.length;
    next = next.filter((r) => incomingById.has(r._id));
    if (next.length !== beforeLength) {
      changed = true;
    }
  }

  // Explicit removals.
  if (removedReports.length) {
    const removedSet = new Set(removedReports);
    const beforeLength = next.length;
    next = next.filter((r) => !removedSet.has(r._id));
    if (next.length !== beforeLength) {
      changed = true;
    }
  }

  return changed ? next : prevReports;
};

/**
 * Merge freshly loaded preview/full reports into the current list, upgrading
 * each matched report to the higher tier. Adds reports that aren't present yet.
 */
export const upgradeReports = (
  prevReports: ReportData[],
  loaded: ReportData[]
): ReportData[] => {
  if (!loaded.length) {
    return prevReports;
  }

  const loadedById = new Map<string, ReportData>();
  loaded.forEach((report) => {
    if (report && report._id) {
      // Respect an explicit dataLevel (e.g. the /:id detail endpoint forces
      // 'full'); otherwise infer it from the payload's fields.
      const stamped = report.dataLevel
        ? (report as ReportData)
        : stampDataLevel(report);
      loadedById.set(report._id, stamped);
    }
  });

  let changed = false;
  const next = prevReports.map((existing) => {
    const update = loadedById.get(existing._id);
    if (!update) {
      return existing;
    }
    loadedById.delete(existing._id);
    changed = true;
    return {
      ...existing,
      ...update,
      dataLevel: higherLevel(existing.dataLevel, update.dataLevel),
      isMinimal:
        higherLevel(existing.dataLevel, update.dataLevel) !== "full",
    };
  });

  // Any loaded reports not already in the list (e.g. deeplink) — append.
  loadedById.forEach((report) => {
    next.push(report);
    changed = true;
  });

  return changed ? next : prevReports;
};

// --- localStorage cold-start cache (status-only pins) ---------------------

interface ReportsCacheSnapshot {
  version: number;
  lastSync?: string | null;
  reports: ReportData[];
}

/** Serialize a minimal-only snapshot for instant cold start. */
export const serializeReportsCache = (
  reports: ReportData[],
  lastSync?: string | null
): string => {
  const snapshot: ReportsCacheSnapshot = {
    version: API_CONFIG.CACHE_VERSION,
    lastSync: lastSync ?? null,
    reports: reports.map((report) => ({
      _id: report._id,
      geoposition: report.geoposition,
      status: report.status,
      sortTimestamp: report.sortTimestamp,
      dataLevel: "minimal" as const,
      isMinimal: true,
    })),
  };
  return JSON.stringify(snapshot);
};

/** Parse a cached snapshot, returning null on absence/version mismatch/error. */
export const deserializeReportsCache = (
  raw: string | null
): { reports: ReportData[]; lastSync: string | null } | null => {
  if (!raw) {
    return null;
  }
  try {
    const snapshot = JSON.parse(raw) as ReportsCacheSnapshot;
    if (
      !snapshot ||
      snapshot.version !== API_CONFIG.CACHE_VERSION ||
      !Array.isArray(snapshot.reports)
    ) {
      return null;
    }
    return {
      reports: snapshot.reports,
      lastSync: snapshot.lastSync ?? null,
    };
  } catch {
    return null;
  }
};
